import React, { createContext, useReducer, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const defaultInitialState = {
  isLoading: false,
  error: null,
  videoUrl: '',
  analysisData: null,
  lastAnalysisDate: null,
  isAutoLoadInProgress: false,
  technicalData: null,
  technicalLoading: false,
  technicalError: null,
  audienceData: null,
  audienceLoading: false,
  audienceError: null
};

const getInitialState = () => {
  try {
    const lastVideoUrl = localStorage.getItem('lastVideoUrl');
    const lastAnalysisDate = localStorage.getItem('lastVideoAnalysisDate');
    const cachedAnalysisData = localStorage.getItem('cachedAnalysisData');

    if (lastVideoUrl) {
      let parsedAnalysisData = null;

      if (cachedAnalysisData) {
        try {
          parsedAnalysisData = JSON.parse(cachedAnalysisData);
          
          if (parsedAnalysisData.video_url && parsedAnalysisData.video_url !== lastVideoUrl) {
            localStorage.removeItem('cachedAnalysisData');
            localStorage.removeItem('lastVideoAnalysisDate');
            parsedAnalysisData = null;
          } else if (parsedAnalysisData.analysis_successful !== true) {
            localStorage.removeItem('cachedAnalysisData');
            localStorage.removeItem('lastVideoAnalysisDate');
            parsedAnalysisData = null;
          } else {
            if (lastAnalysisDate) {
              const analysisDate = new Date(lastAnalysisDate);
              const currentDate = new Date();
              const hoursDifference = (currentDate - analysisDate) / (1000 * 60 * 60);

              if (hoursDifference > 24) {
                // Data is stale but we'll still use it and update later
              }
            }
          }
        } catch (e) {
          localStorage.removeItem('cachedAnalysisData');
          parsedAnalysisData = null;
        }
      }

      return {
        ...defaultInitialState,
        videoUrl: lastVideoUrl,
        analysisData: parsedAnalysisData,
        lastAnalysisDate: parsedAnalysisData ? lastAnalysisDate : null,
        isAutoLoadInProgress: false
      };
    }
  } catch (e) {
    // Error loading from localStorage
  }

  return defaultInitialState;
};

const ActionTypes = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_VIDEO_URL: 'SET_VIDEO_URL',
  SET_ANALYSIS_DATA: 'SET_ANALYSIS_DATA',
  RESET_ANALYSIS: 'RESET_ANALYSIS',
  SET_LAST_ANALYSIS_DATE: 'SET_LAST_ANALYSIS_DATE',
  CLEAR_HISTORY: 'CLEAR_HISTORY',
  SET_AUTO_LOAD_IN_PROGRESS: 'SET_AUTO_LOAD_IN_PROGRESS',
  SET_TECHNICAL_LOADING: 'SET_TECHNICAL_LOADING',
  SET_TECHNICAL_ERROR: 'SET_TECHNICAL_ERROR',
  SET_TECHNICAL_DATA: 'SET_TECHNICAL_DATA',
  RESET_TECHNICAL: 'RESET_TECHNICAL',
  SET_AUDIENCE_LOADING: 'SET_AUDIENCE_LOADING',
  SET_AUDIENCE_ERROR: 'SET_AUDIENCE_ERROR',
  SET_AUDIENCE_DATA: 'SET_AUDIENCE_DATA',
  RESET_AUDIENCE: 'RESET_AUDIENCE'
};

function analysisReducer(state, action) {
  switch (action.type) {
    case ActionTypes.SET_LOADING:
      return {
        ...state,
        isLoading: action.payload,
        error: action.payload ? null : state.error,
      };
    case ActionTypes.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        isLoading: false,
        isAutoLoadInProgress: false,
      };
    case ActionTypes.SET_VIDEO_URL:
      return {
        ...state,
        videoUrl: action.payload,
      };
    case ActionTypes.SET_ANALYSIS_DATA:
      return {
        ...state,
        analysisData: action.payload,
        isLoading: false,
        error: null,
        isAutoLoadInProgress: false,
      };
    case ActionTypes.RESET_ANALYSIS:
      return {
        ...state,
        analysisData: null,
        error: null,
        isAutoLoadInProgress: false,
      };
    case ActionTypes.SET_LAST_ANALYSIS_DATE:
      return {
        ...state,
        lastAnalysisDate: action.payload,
      };
    case ActionTypes.CLEAR_HISTORY:
      return {
        ...defaultInitialState
      };
    case ActionTypes.SET_AUTO_LOAD_IN_PROGRESS:
      return {
        ...state,
        isAutoLoadInProgress: action.payload,
      };
    case ActionTypes.SET_TECHNICAL_LOADING:
      return {
        ...state,
        technicalLoading: action.payload,
        technicalError: action.payload ? null : state.technicalError,
      };
    case ActionTypes.SET_TECHNICAL_ERROR:
      return {
        ...state,
        technicalError: action.payload,
        technicalLoading: false,
      };
    case ActionTypes.SET_TECHNICAL_DATA:
      return {
        ...state,
        technicalData: action.payload,
        technicalLoading: false,
        technicalError: null,
      };
    case ActionTypes.RESET_TECHNICAL:
      return {
        ...state,
        technicalData: null,
        technicalError: null,
        technicalLoading: false,
      };
    case ActionTypes.SET_AUDIENCE_LOADING:
      return {
        ...state,
        audienceLoading: action.payload,
        audienceError: action.payload ? null : state.audienceError,
      };
    case ActionTypes.SET_AUDIENCE_ERROR:
      return {
        ...state,
        audienceError: action.payload,
        audienceLoading: false,
      };
    case ActionTypes.SET_AUDIENCE_DATA:
      return {
        ...state,
        audienceData: action.payload,
        audienceLoading: false,
        audienceError: null,
      };
    case ActionTypes.RESET_AUDIENCE:
      return {
        ...state,
        audienceData: null,
        audienceError: null,
        audienceLoading: false,
      };
    default:
      return state;
  }
}

export const AnalysisContext = createContext(defaultInitialState);

export function AnalysisProvider({ children }) {
  const [state, dispatch] = useReducer(analysisReducer, getInitialState());
  const { t } = useTranslation();

  const setLoading = (isLoading) => {
    dispatch({
      type: ActionTypes.SET_LOADING,
      payload: isLoading,
    });
  };

  const setError = (error) => {
    dispatch({
      type: ActionTypes.SET_ERROR,
      payload: error,
    });
  };

  const setVideoUrl = (url) => {
    if (url && url !== state.videoUrl) {
      localStorage.removeItem('cachedAnalysisData');
      localStorage.removeItem('lastVideoAnalysisDate');
      
      dispatch({
        type: ActionTypes.RESET_ANALYSIS,
      });
    }

    dispatch({
      type: ActionTypes.SET_VIDEO_URL,
      payload: url,
    });

    if (url) {
      localStorage.setItem('lastVideoUrl', url);
    }
  };

  const setAutoLoadInProgress = (inProgress) => {
    dispatch({
      type: ActionTypes.SET_AUTO_LOAD_IN_PROGRESS,
      payload: inProgress,
    });
  };

  const setAnalysisData = (data) => {
    dispatch({
      type: ActionTypes.SET_ANALYSIS_DATA,
      payload: data,
    });

    if (data) {
      let timestampToUse;

      if (data.preserveTimestamp === true) {
        timestampToUse = state.lastAnalysisDate;
      } else if (data.is_full_analysis === true && !data.is_from_cache) {
        timestampToUse = data.analysis_timestamp || new Date().toISOString();
      } else if (data.analysis_timestamp) {
        timestampToUse = data.analysis_timestamp;
      } else if (state.lastAnalysisDate) {
        timestampToUse = state.lastAnalysisDate;
      } else {
        timestampToUse = new Date().toISOString();
      }

      if (timestampToUse) {
        localStorage.setItem('lastVideoAnalysisDate', timestampToUse);
        dispatch({
          type: ActionTypes.SET_LAST_ANALYSIS_DATE,
          payload: timestampToUse
        });
      }

      const isAnalysisSuccessful = !data.error && data.title;
      
      if (isAnalysisSuccessful) {
        try {
          const dataToCache = {
            id: data.id,
            title: data.title,
            channel: data.channel,
            channel_url: data.channel_url,
            upload_date: data.upload_date,
            duration: data.duration,
            duration_formatted: data.duration_formatted,
            view_count: data.view_count,
            like_count: data.like_count,
            comment_count: data.comment_count,
            description: data.description,
            category: data.category,
            category_metadata: data.category_metadata,
            tags: data.tags,
            thumbnail_url: data.thumbnail_url,
            analysis_results: data.analysis_results,
            analysis: data.analysis,
            has_transcription: data.has_transcription,
            analysis_successful: data.analysis_successful,
            analysis_timestamp: timestampToUse,
            cached_at: new Date().toISOString(),
            is_full_analysis: data.is_full_analysis,
            video_url: data.video_url,
            monetization: data.monetization
          };

          localStorage.setItem('cachedAnalysisData', JSON.stringify(dataToCache));
        } catch (e) {
          localStorage.removeItem('cachedAnalysisData');
        }
      } else {
        localStorage.removeItem('cachedAnalysisData');
        localStorage.removeItem('lastVideoAnalysisDate');
      }
    }
  };

  const resetAnalysis = () => {
    dispatch({
      type: ActionTypes.RESET_ANALYSIS,
    });
  };

  const clearHistory = () => {
    localStorage.removeItem('lastVideoUrl');
    localStorage.removeItem('lastVideoAnalysisDate');
    localStorage.removeItem('cachedAnalysisData');

    dispatch({
      type: ActionTypes.CLEAR_HISTORY,
    });
  };

  const setTechnicalLoading = (isLoading) => {
    dispatch({
      type: ActionTypes.SET_TECHNICAL_LOADING,
      payload: isLoading,
    });
  };

  const setTechnicalError = (error) => {
    dispatch({
      type: ActionTypes.SET_TECHNICAL_ERROR,
      payload: error,
    });
  };

  const setTechnicalData = (data) => {
    dispatch({
      type: ActionTypes.SET_TECHNICAL_DATA,
      payload: data,
    });
  };

  const resetTechnical = () => {
    dispatch({
      type: ActionTypes.RESET_TECHNICAL,
    });
  };

  const setAudienceLoading = (isLoading) => {
    dispatch({
      type: ActionTypes.SET_AUDIENCE_LOADING,
      payload: isLoading,
    });
  };

  const setAudienceError = (error) => {
    dispatch({
      type: ActionTypes.SET_AUDIENCE_ERROR,
      payload: error,
    });
  };

  const setAudienceData = (data) => {
    dispatch({
      type: ActionTypes.SET_AUDIENCE_DATA,
      payload: data,
    });
  };

  const resetAudience = () => {
    dispatch({
      type: ActionTypes.RESET_AUDIENCE,
    });
  };

  const value = {
    ...state,
    setLoading,
    setError,
    setVideoUrl,
    setAnalysisData,
    resetAnalysis,
    clearHistory,
    setAutoLoadInProgress,
    setTechnicalLoading,
    setTechnicalError,
    setTechnicalData,
    resetTechnical,
    setAudienceLoading,
    setAudienceError,
    setAudienceData,
    resetAudience
  };

  return (
    <AnalysisContext.Provider value={value}>
      {children}
    </AnalysisContext.Provider>
  );
}

export function useAnalysisContext() {
  const context = useContext(AnalysisContext);
  if (context === undefined) {
    throw new Error('useAnalysisContext must be used within an AnalysisProvider');
  }
  return context;
}  